/**
 * Tax Configuration V2 API service.
 *
 * Promise-based wrapper around the Wave-1 backend endpoints under
 * `api/v1/TaxConfigV2` (TaxConfigV2Controller). Mirrors the axios/base-URL/
 * cookie-auth pattern used by `services/taxConfigApi.js`. All responses are the
 * standard ResponseModel `{ Message, Status, Data }`; the helpers below unwrap
 * `.Data` and throw on a non-success `Status` (APIStatus.Successful === 0) so
 * callers can `await` the inner payload directly and `catch` a friendly Error.
 *
 * NOTE (JS project — no TypeScript): shapes documented via JSDoc typedefs in
 * `utils/taxConfigV2Store.js`. Everything here is camelCase end-to-end (the V2
 * DTOs are camelCase; no snake_case bridge like V1).
 *
 * A "rule" in V2 is a FIELD DECLARATION, not a tax rule: fieldKey + label + category
 * + valueType (text|number|boolean). Questions store their answers against it, Tax
 * Question conditions read it, and Tax Calculate V2 formulas bind to it.
 *
 * Bundle export/import DOES exist at this level (`exportBundle` / `importBundle` below —
 * kind `lhb.taxconfig.v2.bundle`): one file carries a whole config version (header +
 * categories + rules) plus the calc / question versions the user ticked, each embedded as
 * the FULL envelope of its own bundle kind. There is still NO `appendFlag` (out of V2 scope).
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

// Dedicated instance. `withCredentials` is set explicitly (cookie session auth)
// so it does not depend on the global axios default being applied first.
const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/**
 * Unwrap a ResponseModel `{ message, status, data }` → inner `data`.
 * Throws an Error (with `.status`/`.data`) when the envelope reports failure.
 * Accepts either PascalCase (`Status`/`Data`) or camelCase (`status`/`data`).
 */
function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && ('status' in body || 'Status' in body)) {
    const status = 'status' in body ? body.status : body.Status;
    const data = 'data' in body ? body.data : body.Data;
    if (status === API_STATUS_SUCCESS) {
      return data;
    }
    const err = new Error(body.message || body.Message || 'Request failed');
    err.status = status;
    err.data = data;
    throw err;
  }
  return body;
}

/** Normalize an axios/transport error into a friendly Error carrying the API message. */
function toError(error) {
  const body = error && error.response && error.response.data;
  const message =
    (body && (body.message || body.Message)) ||
    (error && error.message) ||
    'Request failed';
  const err = new Error(message);
  err.httpStatus = error && error.response && error.response.status;
  err.status = body && (body.status ?? body.Status);
  err.data = body && (body.data ?? body.Data);
  return err;
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function post(url, payload) {
  try {
    return unwrap(await http.post(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function put(url, payload) {
  try {
    return unwrap(await http.put(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function patch(url, payload) {
  try {
    return unwrap(await http.patch(url, payload));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}
async function del(url) {
  try {
    return unwrap(await http.delete(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}

const ROOT = 'api/v1/TaxConfigV2';

export const taxConfigV2Api = {
  // ── Tax Years (global, multi-active) ─────────────────────────────────
  /** @returns {Promise<Object[]>} TaxYearV2Dto[] */
  getYears(includeInactive = true) {
    return get(`${ROOT}/years?includeInactive=${includeInactive}`);
  },
  /** @returns {Promise<Object>} TaxYearV2Dto */
  getYear(id) {
    return get(`${ROOT}/years/${id}`);
  },
  /** @returns {Promise<Object>} TaxYearV2Dto (404 → thrown Error) */
  getYearByCode(code) {
    return get(`${ROOT}/years/code/${encodeURIComponent(code)}`);
  },
  /** @returns {Promise<Object>} created TaxYearV2Dto */
  createYear(body) {
    return post(`${ROOT}/years`, body);
  },
  /** @returns {Promise<Object>} updated TaxYearV2Dto */
  updateYear(id, body) {
    return put(`${ROOT}/years/${id}`, body);
  },
  /** @returns {Promise<boolean>} (years are multi-active — no sibling deactivation) */
  setYearActive(id, isActive) {
    return patch(`${ROOT}/years/${id}/active`, { isActive: Boolean(isActive) });
  },
  /** @returns {Promise<boolean>} (cascade-deletes versions→categories→rules) */
  deleteYear(id) {
    return del(`${ROOT}/years/${id}`);
  },

  // ── Config Versions (single-active-per-year, clone, full) ────────────
  /** @returns {Promise<Object[]>} TaxConfigVersionV2Dto[] */
  getVersions(yearId = null, includeInactive = true) {
    let url = `${ROOT}/versions?includeInactive=${includeInactive}`;
    if (yearId != null) url += `&yearId=${yearId}`;
    return get(url);
  },
  /** @returns {Promise<Object>} TaxConfigVersionV2Dto */
  getVersion(id) {
    return get(`${ROOT}/versions/${id}`);
  },
  /** @returns {Promise<Object>} created TaxConfigVersionV2Dto */
  createVersion(body) {
    return post(`${ROOT}/versions`, body);
  },
  /** @returns {Promise<Object>} updated TaxConfigVersionV2Dto */
  updateVersion(id, body) {
    return put(`${ROOT}/versions/${id}`, body);
  },
  /** @returns {Promise<boolean>} (siblings auto-deactivated server-side) */
  setVersionActive(id, isActive) {
    return patch(`${ROOT}/versions/${id}/active`, { isActive: Boolean(isActive) });
  },
  /** @returns {Promise<boolean>} (cascade categories+rules; no delete guard on active) */
  deleteVersion(id) {
    return del(`${ROOT}/versions/${id}`);
  },
  /** @returns {Promise<Object>} new (cloned) TaxConfigVersionV2Dto */
  cloneVersion(id, body) {
    return post(`${ROOT}/versions/${id}/clone`, body);
  },
  /** @returns {Promise<Object>} TaxConfigV2Full { version, categories:[{…, rules:[…]}] } */
  getFullConfig(versionId, includeInactive = true) {
    return get(`${ROOT}/versions/${versionId}/full?includeInactive=${includeInactive}`);
  },

  // ── Categories (scoped to a version) ─────────────────────────────────
  /** @returns {Promise<Object[]>} TaxCategoryV2Dto[] */
  getCategories(versionId, includeInactive = true) {
    return get(`${ROOT}/versions/${versionId}/categories?includeInactive=${includeInactive}`);
  },
  /** @returns {Promise<Object>} TaxCategoryV2Dto */
  getCategory(id) {
    return get(`${ROOT}/categories/${id}`);
  },
  /** @returns {Promise<Object>} created TaxCategoryV2Dto */
  createCategory(versionId, body) {
    return post(`${ROOT}/versions/${versionId}/categories`, body);
  },
  /** @returns {Promise<Object>} updated TaxCategoryV2Dto */
  updateCategory(id, body) {
    return put(`${ROOT}/categories/${id}`, body);
  },
  /** @returns {Promise<boolean>} */
  setCategoryActive(id, isActive) {
    return patch(`${ROOT}/categories/${id}/active`, { isActive: Boolean(isActive) });
  },
  /** @returns {Promise<boolean>} (its rules become uncategorized in /full) */
  deleteCategory(id) {
    return del(`${ROOT}/categories/${id}`);
  },

  // ── Rules (scoped to a version; bulk-save = full replace) ────────────
  /** @returns {Promise<Object[]>} TaxConfigRuleV2Dto[] */
  getRules(versionId, categoryCode = null, includeInactive = true) {
    let url = `${ROOT}/versions/${versionId}/rules?includeInactive=${includeInactive}`;
    if (categoryCode) url += `&categoryCode=${encodeURIComponent(categoryCode)}`;
    return get(url);
  },
  /** @returns {Promise<Object>} TaxConfigRuleV2Dto */
  getRule(ruleId) {
    return get(`${ROOT}/rules/${ruleId}`);
  },
  /** @returns {Promise<Object>} created TaxConfigRuleV2Dto */
  createRule(versionId, body) {
    return post(`${ROOT}/versions/${versionId}/rules`, body);
  },
  /** @returns {Promise<Object>} updated TaxConfigRuleV2Dto */
  updateRule(ruleId, body) {
    return put(`${ROOT}/rules/${ruleId}`, body);
  },
  /** @returns {Promise<boolean>} */
  setRuleActive(ruleId, isActive) {
    return patch(`${ROOT}/rules/${ruleId}/active`, { isActive: Boolean(isActive) });
  },
  /** @returns {Promise<boolean>} */
  deleteRule(ruleId) {
    return del(`${ROOT}/rules/${ruleId}`);
  },
  /**
   * Replace the whole rule set of a version (form editor "Save") — transactional
   * all-or-nothing. On a 400 the whole batch rolls back (nothing persisted).
   * @param {number} versionId
   * @param {Object[]} rulesArray array of CreateTaxConfigRuleV2Request-shaped objects
   * @returns {Promise<Object[]>} TaxConfigRuleV2Dto[] (the new set)
   */
  bulkSaveRules(versionId, rulesArray) {
    return put(`${ROOT}/versions/${versionId}/rules/bulk`, { rules: rulesArray || [] });
  },

  // ── Export / Import (move a WHOLE config version between machines) ───
  /**
   * @typedef {Object} TaxConfigBundleV2Dto
   * @property {string} schemaVersion `"1.0"`
   * @property {string} kind `"lhb.taxconfig.v2.bundle"` — sniffed by the import dialog BEFORE anything else
   * @property {?string} exportedAt @property {?string} exportedBy
   * @property {{taxYearCode:?string, beYear:?number, configVersionCode:?string, description:?string}} source
   *   display-only provenance — never resolves the target
   * @property {{versionCode:string, description:?string, effectiveStartDate:?string, effectiveEndDate:?string}} configVersion
   * @property {{categoryCode:string, label:?string, sortOrder:number, isActive:boolean}[]} categories
   * @property {{categoryCode:?string, fieldKey:string, label:?string, sortOrder:number, isActive:boolean, valueType:?string}[]} rules
   * @property {Object[]} calcVersions FULL `lhb.taxcalc.v2.bundle` envelopes (`{kind, source:{calcVersionCode,…}, functions:[…]}`)
   * @property {Object[]} questionVersions FULL `lhb.taxquestion.v2.bundle` envelopes (`{kind, source:{questionVersionCode,…}, steps, items}`)
   *
   * @typedef {Object} ImportTaxConfigBundleV2Result
   * @property {number} yearID @property {?number} versionID `null` on a dry run
   * @property {string} versionCode @property {boolean} versionCodeAdjusted
   * @property {number} categoryCount @property {number} ruleCount
   * @property {{sourceCode:string, versionCode:string, versionCodeAdjusted:boolean, functionCount:number, compileErrorCount:number}[]} calcVersions
   * @property {{sourceCode:string, versionCode:string, versionCodeAdjusted:boolean, stepCount:number, itemCount:number, droppedCalcConfigCount:number, droppedCalcFunctionCount:number}[]} questionVersions
   * @property {number} totalDroppedCalcConfigCount CALCULATE items that will NOT compute (their calc version is not in the file)
   * @property {number} totalDroppedCalcFunctionCount
   * @property {number} totalCompileErrorCount always 0 on a dry run — the preview does not compile
   * @property {boolean} dryRun @property {string[]} warnings
   */
  /**
   * Export one config version as a portable bundle: header + ALL categories + ALL rules
   * (inactive rows included, always) + ONLY the calc / question versions whose ids are passed.
   * An id that does not belong to this config version is a 400, not a silent skip.
   *
   * Returns the bundle OBJECT (a normal `ResponseModel`, not a file download) — the caller
   * turns it into a `.json` download with a Blob. POST, because the selection travels in the body.
   *
   * @param {number} versionId source config version id (404 → rejected Error)
   * @param {Object} [selection]
   * @param {number[]} [selection.calcVersionIds=[]] calc versions to embed (empty = none)
   * @param {number[]} [selection.questionVersionIds=[]] question versions to embed (empty = none)
   * @returns {Promise<TaxConfigBundleV2Dto>}
   */
  exportBundle(versionId, { calcVersionIds = [], questionVersionIds = [] } = {}) {
    return post(`${ROOT}/versions/${versionId}/export`, {
      calcVersionIds: (calcVersionIds || []).map(Number),
      questionVersionIds: (questionVersionIds || []).map(Number),
    });
  },
  /**
   * Import a bundle as a NEW config version (always inactive, children inactive too) under
   * ONE tax year. Write order server-side: version → categories → rules → calc versions →
   * question versions; if anything fails after the version row exists, the server deletes it
   * again (cascade) — a half-written version is never left behind.
   *
   * `dryRun: true` writes NOTHING and calls NO child import: it is an in-file cross-check
   * (FieldKeys vs `rules[]`, question `calcConfig` vs `calcVersions[]`, code collision) plus
   * counts. It does NOT compile formulas — `totalCompileErrorCount` is only meaningful on the
   * real run. Read `warnings` / `totalDroppedCalcConfigCount` and surface them; a silent partial
   * load is worse than a refusal.
   *
   * @param {Object} args
   * @param {TaxConfigBundleV2Dto} args.bundle The parsed file. Untrusted input — only its content
   *   is used; `bundle.source.*` is display-only and never resolves the target.
   * @param {number} args.yearID Target tax year — the row whose «นำเข้า» button was pressed,
   *   NEVER the file's `source.taxYearCode`. 🔴 Key is `yearID` (trailing capital ID) and is
   *   forwarded verbatim: ASP.NET's camelCase policy binds `yearID`, and a wrong casing binds
   *   nothing (silent null).
   * @param {?string} [args.versionCode] Code for the new config version. Empty ⇒ the file's
   *   `configVersion.versionCode`. Either way a collision auto-increments (`V1` → `V1-2`) and the
   *   result reports `versionCodeAdjusted: true`.
   * @param {?string} [args.description] Empty ⇒ the file's `configVersion.description`.
   * @param {boolean} [args.dryRun=false] see above — always sent as a boolean.
   * @returns {Promise<ImportTaxConfigBundleV2Result>}
   */
  importBundle({ bundle, yearID, versionCode, description, dryRun } = {}) {
    const code = String(versionCode || '').trim();
    const desc = String(description || '').trim();
    return post(`${ROOT}/import`, {
      bundle,
      yearID,
      versionCode: code || null,
      description: desc || null,
      dryRun: Boolean(dryRun),
    });
  },
};

export default taxConfigV2Api;
